import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function PendingApprovalPage() {
  const { logout, user } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          title: 'حسابك قيد المراجعة',
          description: 'تم إنشاء حسابك بنجاح وهو الآن قيد المراجعة من قبل إدارة النظام'
        };
      case 'rejected':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'تم رفض طلب التسجيل',
          description: 'عذراً، تم رفض طلب تسجيلك. يرجى التواصل مع إدارة النظام للمزيد من المعلومات'
        };
      default:
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          title: 'تم تفعيل حسابك',
          description: 'تم الموافقة على حسابك بنجاح'
        };
    }
  };

  const employeeData = user?.employeeData;
  const status = employeeData?.approval_status || 'pending';
  const statusInfo = getStatusInfo(status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">نظام إدارة شركة السياحة</h1>
        </div>

        <Card className={`${statusInfo.bgColor} ${statusInfo.borderColor}`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${statusInfo.color}`}>
              <StatusIcon className="w-5 h-5" />
              {statusInfo.title}
            </CardTitle>
            <CardDescription className="text-gray-700">
              {statusInfo.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {employeeData && (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="font-medium">الاسم:</span> {employeeData.name}
                </div>
                <div className="text-sm">
                  <span className="font-medium">البريد الإلكتروني:</span> {employeeData.email}
                </div>
                <div className="text-sm">
                  <span className="font-medium">المنصب:</span> {employeeData.position}
                </div>
                <div className="text-sm">
                  <span className="font-medium">القسم:</span> {employeeData.department}
                </div>
              </div>
            )}

            {status === 'pending' && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  سيتم إشعارك عبر البريد الإلكتروني عند الموافقة على حسابك. تحقق من بريدك بانتظام.
                </AlertDescription>
              </Alert>
            )}

            {status === 'rejected' && employeeData?.rejection_reason && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-1">سبب الرفض:</div>
                  {employeeData.rejection_reason}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="flex-1"
              >
                تسجيل الخروج
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                تحديث الحالة
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-xs text-blue-700 text-center space-y-1">
              <p>• للاستفسارات، تواصل مع إدارة النظام</p>
              <p>• يتم مراجعة طلبات التسجيل خلال 24-48 ساعة</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}